import React from 'react'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import type { RestaurantCancellationCard as CancellationCardType } from '@/models/restaurant-manager'
import { LinkedText } from '@/components/molecules/linked-text'

type Props = {
  cancellation: CancellationCardType
  isConfirming: boolean
  onConfirm: (reservationId: string, salesDate: string, cancelledAt: string, isConfirmed: boolean) => void
}

export const RestaurantCancellationCard: React.FC<Props> = ({ cancellation, isConfirming, onConfirm }) => {
  const { t } = useTranslation()

  return (
    <article css={[cardStyle, !cancellation.isConfirmed && pendingCardStyle]}>
      <div css={cardMainStyle}>
        <div css={cardHeaderStyle}>
          <LinkedText
            guestName={cancellation.guestName}
            reservationUuid={cancellation.reservationUuid}
            reservationId={cancellation.reservationId}
            isOpenInNewTab={true}
            customTextStyle={guestNameStyle}
            customLinkStyle={guestNameLinkStyle}
          />
          {cancellation.isConfirmed ? (
            <button
              type="button"
              css={confirmedButtonStyle}
              disabled={isConfirming}
              onClick={() => onConfirm(cancellation.reservationId, cancellation.salesDate, cancellation.cancelledAt, false)}
            >
              {t('RestaurantManager.ConfirmedStatus')}
            </button>
          ) : (
            <button
              type="button"
              css={completeButtonStyle}
              disabled={isConfirming}
              onClick={() => onConfirm(cancellation.reservationId, cancellation.salesDate, cancellation.cancelledAt, true)}
            >
              {t('RestaurantManager.MarkAsConfirmed')}
            </button>
          )}
        </div>

        <div css={subjectListStyle}>
          {cancellation.cancelledSubjects.map(subject => (
            <div key={`${cancellation.reservationId}-${subject.subjectName}`} css={subjectTextStyle}>
              {`${subject.subjectName} ×${subject.quantity}`}
            </div>
          ))}
        </div>
      </div>

      <div css={cardFooterStyle}>
        {t('RestaurantManager.CancellationDate')}: {dayjs(cancellation.cancelledAt).format('M/D H:mm')}
      </div>
    </article>
  )
}

const cardStyle = css({
  background: '#FFFFFF',
  border: '1px solid #D6D6D6',
  borderRadius: 8,
  overflow: 'hidden',
})

const pendingCardStyle = css({
  background: '#F7F7F7',
})

const cardMainStyle = css({
  padding: '14px 16px 12px',
})

const cardHeaderStyle = css({
  alignItems: 'flex-start',
  display: 'flex',
  gap: 12,
  justifyContent: 'space-between',
  marginBottom: 10,
})

const guestNameStyle = css({
  color: '#272727',
  fontSize: 14,
  fontWeight: 'bold',
  lineHeight: 1.5,
})

const guestNameLinkStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  lineHeight: 1.5,
})

const subjectListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
})

const subjectTextStyle = css({
  color: '#272727',
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
})

const cardFooterStyle = css({
  borderTop: '1px solid #D6D6D6',
  color: '#676767',
  fontSize: 11,
  lineHeight: 1.4,
  padding: '8px 16px',
})

const completeButtonStyle = css({
  background: '#FFFFFF',
  border: '1px solid #F2A40B',
  borderRadius: 18,
  color: '#F2A40B',
  cursor: 'pointer',
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 'bold',
  lineHeight: 1,
  minWidth: 64,
  padding: '10px 16px',
})

const confirmedButtonStyle = css({
  alignItems: 'center',
  background: '#D0D0D0',
  border: 'none',
  borderRadius: 18,
  color: '#FFFFFF',
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 'bold',
  justifyContent: 'center',
  lineHeight: 1,
  minWidth: 80,
  padding: '10px 16px',
})
